import { findFocusableAncestor } from "@prismicio/editor-support/DOM"
import clsx from "clsx"
import {
  createContext,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useContext,
} from "react"

import styles from "./Table.module.css"

export interface TableProps {
  children: ReactNode
  /** The number and size of columns. See [grid-template-columns](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns). */
  columnLayout: string
  isLoading?: boolean
  isInteractive?: boolean
  border?: boolean
  radius?: boolean
}

/**
 * Outer table component. This should wrap any table header and body in order to
 * set the layout context.
 */
export function Table(props: TableProps) {
  const {
    children,
    columnLayout,
    isLoading = false,
    isInteractive = true,
    radius = true,
    border = true,
  } = props

  return (
    <div
      className={clsx(
        styles.root,
        border && styles.border,
        radius && styles.radius,
      )}
    >
      <TableContext.Provider value={{ columnLayout, isLoading, isInteractive }}>
        {children}
      </TableContext.Provider>
    </div>
  )
}

interface TableContextValue {
  columnLayout: string
  isLoading: boolean
  isInteractive: boolean
}

const TableContext = createContext<TableContextValue>({
  columnLayout: "1fr",
  isLoading: false,
  isInteractive: true,
})

function useTable() {
  return useContext(TableContext)
}

/**
 * @internal
 * Context used to recognise the section of the table a row and cell are in.
 * This is used to apply the correct styling.
 */
type TableSection = "head" | "body"

const TableSectionContext = createContext<TableSection>("body")

function useTableSection() {
  return useContext(TableSectionContext)
}

export interface TableHeadProps {
  children: ReactNode
}

/**
 * Wrapper for the entire table header. This ensures any rows contained in it have the
 * header row style applied.
 */
export function TableHead(props: TableHeadProps) {
  return (
    <TableSectionContext.Provider value="head">
      {props.children}
    </TableSectionContext.Provider>
  )
}

export interface TableBodyProps {
  children: ReactNode
}

/**
 * Wrapper for the entire table body and its rows. This ensures any rows contained in
 * it have the body row style applied.
 */
export function TableBody(props: TableBodyProps) {
  return (
    <TableSectionContext.Provider value="body">
      {props.children}
    </TableSectionContext.Provider>
  )
}

export interface ClickableTableRowProps {
  children: ReactNode
  /** Visibly disable the row */
  disabled?: boolean
  /** Callback fired when the row is clicked */
  onClick?: () => void
  renderLink?: never
}

export interface LinkTableRowProps {
  children: ReactNode
  /** Visibly disable the row */
  disabled?: boolean
  onClick?: never
  /** Anchor tag wrapper for the row to enable using any routing library such as next Link or react-router. */
  renderLink: (children: ReactNode) => JSX.Element
}

type TableRowProps = ClickableTableRowProps | LinkTableRowProps

/**
 * A possibly interactable table row. This component is used in
 * both the header and the table body. The interactivity works in 1 of 3 ways:
 *
 * - Not giving any props other than children: The table acts as a static element which you can't interact with
 * - Given an onClick prop: fires the onClick callback when the row is clicked
 * - Given a renderLink: wraps the **entire** row in the given component. E.g. wrapping it in `react-router-dom` `<Link>`
 */
export function TableRow(props: TableRowProps) {
  const { renderLink } = props

  const { rowContent } = useTableRowContent(props)
  const { isLoading } = useTable()

  if (!renderLink || isLoading) return rowContent

  const link = <div className={styles.link} />

  return (
    <div className={styles.linkedRowContainer}>
      <div className={styles.linkElementWrapper}>{renderLink(link)}</div>
      {rowContent}
    </div>
  )
}

export interface TableCellProps {
  children?: ReactNode
  /** How to align the content in the cell. Note that undefined defaults to start */
  align?: "center" | "end"
  /** Whether the content of the cell is interactive, this disables the link properties for that cell. Defaults to `false`. */
  interactive?: boolean
}

/**
 * Table cells wrap the individual content of the table.
 */
export function TableCell(props: TableCellProps) {
  const { children, align, interactive = false } = props
  const section = useTableSection()
  const { isLoading } = useTable()

  const className = clsx(
    styles.cell,
    styles.overflow,
    section === "body" ? styles.bodyCell : styles.headerCell,
    isLoading && section === "body" && styles.loadingCell,
    align && styles[`align-${align}`],
  )

  return (
    <div
      className={className}
      style={{ pointerEvents: interactive ? "auto" : undefined }}
    >
      {children}
    </div>
  )
}

/**
 * Hook that provides the row content.
 * @returns rowContent - the div wrapping the children given in props. This is to add the optional onClick (see {@link TableRow}) and styles.
 */
function useTableRowContent(params: TableRowProps) {
  const { children, onClick: onClickCallback, renderLink, disabled } = params

  const section = useTableSection()
  const { columnLayout, isLoading, isInteractive } = useTable()

  function onClick(event: ReactMouseEvent<HTMLDivElement, MouseEvent>) {
    executeClickCallback(event, onClickCallback)
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.code === "Enter" || event.code === "Space") {
      executeClickCallback(event, onClickCallback)
    }
  }

  function executeClickCallback(
    event: ReactMouseEvent<HTMLDivElement, MouseEvent> | KeyboardEvent,
    onClickCallback: (() => void) | undefined,
  ) {
    if (!onClickCallback) return

    const target = event.target as HTMLElement

    if (!event.currentTarget.contains(target)) return

    const focusableAncestor = findFocusableAncestor(target)

    if (focusableAncestor?.contains(event.currentTarget) ?? true) {
      onClickCallback()
    }
  }

  const isStatic = (!onClickCallback && !renderLink) || isLoading

  const className = clsx(
    section === "body" ? styles.bodyRow : styles.header,
    isInteractive && styles.interactive,
    Boolean(onClickCallback) && styles.onClickBodyRow,
    Boolean(renderLink) && styles.disablePointerEvents,
    isStatic && section === "body" && styles.staticBodyRow,
    disabled && styles.disabledRow,
  )

  const rowContent = (
    <div
      className={className}
      style={{ gridTemplateColumns: columnLayout }}
      onClick={onClick}
      role={onClickCallback ? "button" : undefined}
      tabIndex={onClickCallback ? 0 : -1}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  )

  return {
    rowContent,
  }
}
